import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { routePathName, routeUrl } from '@app/app.models';
import { FeedbackService } from '@services/feedback';
import { ProgressIndicatorService } from '@services/progress';
import { Observable, of } from 'rxjs';
import { catchError, mergeMap, take } from 'rxjs/operators';

import { IAuthToken, PermissionCognitoGroups, TokenService } from '../token';

/**
 * Router guard for locking down access to specific Cognito groups. Will read of the data property
 * and validate the provided group(s) against the auth users JWT and provide access if either one
 * exists.
 *
 * Usage in Router:
 * const routes: Routes = [
 * 	{
 * 		path: '/protected-path',
 * 		component: ProtectedComponent,
 * 		pathMatch: 'full',
 * 		canActivate: [CognitoRoleGuardService],
 * 		data: {
 * 			cognitoRoles: [PermissionCognitoGroups.registrations]
 * 		}
 * 	}
 * ];
 */
@Injectable({
	providedIn: 'root'
})
export class CognitoRoleGuardService implements CanActivate {
	constructor(
		public router: Router,
		private feedbackService: FeedbackService,
		private tokenService: TokenService,
		private progressIndicatorService: ProgressIndicatorService
	) {}

	public canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
		const cognitoRoles = route.data.cognitoRoles as Array<PermissionCognitoGroups> | [];

		this.progressIndicatorService.spinner(true);

		return this.tokenService.getDecodedToken().pipe(
			take(1),
			mergeMap((decodedToken: IAuthToken) => {
				for (const cognitoRole of cognitoRoles) {
					if (decodedToken['roles'].includes(cognitoRole)) {
						this.progressIndicatorService.spinner(false);
						return of(true);
					}
				}

				return this.handleError();
			}),
			catchError(this.handleError)
		);
	}

	private handleError(error?: any): Observable<boolean> {
		if (error) this.feedbackService.apiError(error);

		this.progressIndicatorService.spinner(false);
		this.router.navigate(routeUrl(''));

		return of(false);
	}
}
